import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Typography,
  Box,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { ShoppingCartItem as ShoppingCartItemType } from "../models/GiftBrand";
import { useAppDispatch } from "../hooks/hooks";
import {
  removeGiftBrand,
  updateQuantity,
  updateAmount,
} from "../redux/giftShoppingCartSlice";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

interface ShoppingCartItemProps {
  item: ShoppingCartItemType;
}

const ShoppingCartItem: React.FC<ShoppingCartItemProps> = ({ item }) => {
  const dispatch = useAppDispatch();

  const handleRemoveItem = () => {
    dispatch(removeGiftBrand(item.productId));
  };

  const handleIncreaseQuantity = () => {
    dispatch(
      updateQuantity({ productId: item.productId, quantity: item.quantity + 1 })
    );
  };

  const handleDecreaseQuantity = () => {
    if (item.quantity > 1) {
      dispatch(
        updateQuantity({
          productId: item.productId,
          quantity: item.quantity - 1,
        })
      );
    } else {
      handleRemoveItem();
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = parseFloat(e.target.value);
    if (!isNaN(newAmount)) {
      dispatch(updateAmount({ productId: item.productId, amount: newAmount }));
    }
  };

  return (
    <>
      <ListItem
        secondaryAction={
          <IconButton edge="end" aria-label="delete" onClick={handleRemoveItem}>
            <DeleteIcon color="error" />
          </IconButton>
        }
      >
        <ListItemAvatar>
          <Avatar
            variant="rounded"
            sx={{ width: 80, height: 60, mr: 2 }}
            alt={item.brandName}
            src={item.productImage}
          />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography variant="subtitle1" fontWeight="medium">
              {item.brandName}
            </Typography>
          }
          secondary={
            <>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mr: 1 }}
                >
                  Quantity:
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleDecreaseQuantity}
                  aria-label="reduce quantity"
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ mx: 1 }}>
                  {item.quantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleIncreaseQuantity}
                  aria-label="increase quantity"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box mb={1}>
                <TextField
                  select
                  label="Amount"
                  value={item.amount}
                  onChange={handleAmountChange}
                  size="small"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {item.currency}
                      </InputAdornment>
                    ),
                  }}
                >
                  {item.denominations.map((denomination) => (
                    <MenuItem key={denomination} value={denomination}>
                      {denomination}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Typography variant="body2" fontWeight="medium">
                Total: {(item.amount * item.quantity).toFixed(2)}{" "}
                {item.currency}
              </Typography>
            </>
          }
        />
      </ListItem>
    </>
  );
};

export default ShoppingCartItem;
